// src/components/chat/chat.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as Selection from 'selection-popover';
import { db, functions, auth, app } from '../../firebase';
import {
  query,
  where,
  getDocs,
  orderBy,
  doc,
  deleteDoc,
  limit,
  addDoc,
  collection,
  serverTimestamp,
  getDoc,
  onSnapshot,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { httpsCallable, connectFunctionsEmulator, getFunctions } from "firebase/functions";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import PathLibrary from './PathLibrary';
import Skeleton from 'react-loading-skeleton';
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ThreeDotLoader, StaticFiveDots } from '../loaders/Loaders';
import { FileText } from 'lucide-react';
import CompAISpeakIt from '../CompAISpeakIt';
import { UserAuth } from '/context/AuthContext';
import Bookmark from './Bookmark';
import BookmarkFavorites from './BookmarkFavorites';
import HamburgerButton from './HamburgerButton';
import BookmarkManager from './BookmarkManager';
import BookmarkQuickLinks from './BookmarkQuickLinks';
import { logCustomEvent } from '../UtilAnalytics';
import PathCastPlayer from '../PathCastPlayer';

const Chat = ({ contextId: initialContextId, intelligenceFoundation }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [contextData, setContextData] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [contextId, setContextId] = useState(initialContextId);
  const [isPathLibraryOpen, setIsPathLibraryOpen] = useState(false);
  const [micError, setMicError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const prevListeningRef = useRef(false);
  const textareaRef = useRef(null);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [isRecreatingContext, setIsRecreatingContext] = useState(false);
  const [isBookmarkModalOpen, setIsBookmarkModalOpen] = useState(false);
  const [bookmarkContent, setBookmarkContent] = useState('');
  const [isBookmarkManagerOpen, setIsBookmarkManagerOpen] = useState(false);
  const [shouldRefocusInput, setShouldRefocusInput] = useState(false);
  const [isCreatingPathCast, setIsCreatingPathCast] = useState(false);

  // PathCast audio state
  const [pathCastAudioUrl, setPathCastAudioUrl] = useState(null);

  // Loader status state
  const [loaderStatusId, setLoaderStatusId] = useState(null);
  const [loaderStatusLabel, setLoaderStatusLabel] = useState(null);

  // Move the UserAuth hook to component level
  const { user } = UserAuth();

  // Speech recognition hook
  const {
    transcript,
    interimTranscript,
    finalTranscript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();


  const functionsInstance = getFunctions(app);
  if (process.env.NODE_ENV === 'development') {
    connectFunctionsEmulator(functionsInstance, "127.0.0.1", 5001);
  }

  // Function references using modern Firebase SDK
  const createContextCache = httpsCallable(functionsInstance, 'createContextCache');
  const queryContextCache = httpsCallable(functionsInstance, 'queryContextCache');
  const getContextHistory = httpsCallable(functionsInstance, 'getContextHistory');
  const createPathCastFirebase = httpsCallable(functionsInstance, 'createPathCast');

  // State for ALL bookmarks for the manager
  const [allBookmarks, setAllBookmarks] = useState([]);
  const [isBookmarksLoading, setIsBookmarksLoading] = useState(false); // Loading state for all bookmarks

  // State for favorite bookmarks (remains unchanged)
  const [favoriteBookmarks, setFavoriteBookmarks] = useState([]);
  const [isBookmarksOpen, setIsBookmarksOpen] = useState(false); // For the favorites dropdown

  // Toggle bookmarks menu state
  const toggleBookmarksMenu = () => {
    setIsBookmarksOpen(prev => !prev);
  };



  // Function to fetch favorite bookmarks from Firebase
  const fetchFavoriteBookmarks = useCallback(async () => {
    try {
      if (!user) {
        console.error('No user is signed in');
        return [];
      }

      // Query Firestore for bookmarks marked as favorite for the current user
      const bookmarksQuery = query(
        collection(db, "apps"),
        where("userId", "==", user.uid),
        where("favorite", "==", true)
      );

      const querySnapshot = await getDocs(bookmarksQuery);

      // Map the documents to a more usable format
      const favoriteBookmarks = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFavoriteBookmarks(favoriteBookmarks);

      return favoriteBookmarks;
    } catch (error) {
      //console.error('Error fetching favorite bookmarks:', error);
      return [];
    }
  }, [user]);

  useEffect(() => {
    fetchFavoriteBookmarks();
  }, [user]);

  // Function to handle bookmark selection and populate the textarea
  const handleSelectBookmark = (bookmark, submitForm = false) => {
    if (bookmark && bookmark.content) {
      const contentToUse = bookmark.content; // Store content in a variable

      // Set the input value to the bookmark content (optional, but good for consistency if not submitting)
      setInput(contentToUse);

      // Close the bookmarks menu
      setIsBookmarksOpen(false);

      // Focus the textarea (might happen after submit starts, which is fine)
      if (textareaRef.current) {
        textareaRef.current.focus();
      }

      // If submitForm is true, submit the form *passing the content directly*
      if (submitForm) {
        // Pass null for the event, and the content as textToSubmit
        handleSubmit(null, contentToUse); // Changed this line
      }
    } else {
      //console.error('Selected bookmark has no content');
    }
  };



  const baseColor = "#374151";
  const highlightColor = "#4b5563";

  // Log transcripts on every render
  // console.log(`Render - Listening: ${listening}, Mic Available: ${isMicrophoneAvailable}`);
  // console.log(`  Transcript: '${transcript}'`);
  // console.log(`  Interim: '${interimTranscript}'`);
  // console.log(`  Final: '${finalTranscript}'`);

  useEffect(() => {
    //console.log("Transcript changed:", transcript);
    setInput(transcript);
  }, [transcript]);

  useEffect(() => {
    if (!isMicrophoneAvailable) {
      //console.error("Microphone is not available or permission denied.");
      setMicError("Microphone access denied or unavailable. Please check browser permissions.");
    } else {
      setMicError(null);
    }
  }, [isMicrophoneAvailable]);

  useEffect(() => {
    // If no contextId is provided, create a new one
    const initializeContext = async () => {
      if (!initialContextId) {
        try {
          setLoading(true);
          //          console.log('intelligenceFoundation', intelligenceFoundation);
          // Pass timeout options to the httpsCallable function
          const result = await createContextCache(
            { intelligenceFoundation },
            { timeout: 120000 } // 120 seconds timeout
          );

          // Check if the result has valid data before using it
          if (result && result.data && result.data.contextId) {
            setContextId(result.data.contextId);
          } else {
            //console.error('Error: Received invalid context data structure');
            // Handle this case gracefully - maybe set a default context or show error message
            setMessages(prev => [...prev, {
              type: 'assistant',
              contentType: 'text',
              content: 'Error initializing chat. Please try again later.'
            }]);
          }
          setLoading(false);
        } catch (error) {
          //console.error('Error creating context cache:', error);
          // Show user-friendly error message
          setMessages(prev => [...prev, {
            type: 'assistant',
            contentType: 'text',
            content: 'Unable to initialize chat. Please try again later.'
          }]);
          setLoading(false);
        }
      }
    };

    initializeContext();
  }, [initialContextId]);

  useEffect(() => {
    // If we have a contextId, load the context and conversation history
    if (contextId) {
      loadContextHistory();
    }
  }, [contextId]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadContextHistory = async () => {
    try {
      setLoading(true);
      const result = await getContextHistory({ contextId });
      setContextData(result.data.contextData);

      // Format conversation history into messages
      const conversationHistory = result.data.conversations.map(conv => [
        { type: 'user', contentType: 'text', content: conv.query },
        { type: 'assistant', contentType: 'text', content: conv.response }
      ]).flat();

      // Filter out potential "Generating podcast script..." messages from history
      const filteredHistory = conversationHistory.filter(msg => !(msg.type === 'user' && msg.content === 'Generating podcast script...'));

      // First set the conversation history
      setMessages(filteredHistory);


      // Then add welcome message with a slight delay for animation effect
      setTimeout(() => {
        const welcomeMessage = {
          type: 'assistant',
          contentType: 'text',
          content: 'Hello! What did you want to jam on today?'
        };

        // Only add welcome message if no history exists
        if (filteredHistory.length === 0) {
          setMessages(prev => [...prev, welcomeMessage]);
          playSound();
        }
      }, 800); // Adjusted delay slightly

      setLoading(false);
    } catch (error) {
      //console.error('Error loading context history:', error);
      setLoading(false);
      // Add error message to chat
      setMessages([{
        type: 'assistant',
        contentType: 'text',
        content: 'There was an issue loading the chat history. Please try refreshing.'
      }]);
    }
  };

  const playSound = () => {
    //if (storyTone.notificationTone === 'none') return;
    const audio = new Audio(`/media/notifications/notification-sound-269266.mp3`);
    audio.play();
  };

  // Define handleSubmit FIRST
  const handleSubmit = useCallback(async (e, textToSubmit = null) => {
    e?.preventDefault(); // Allow preventing default if event exists

    // Determine the message content: prioritize textToSubmit, then input state
    const messageToSend = textToSubmit !== null ? textToSubmit.trim() : input.trim();

    logCustomEvent('chat_message_sent', {});

    if (!messageToSend || !contextId) {
      //console.log("handleSubmit returning early. Message:", messageToSend, "Context ID:", contextId);
      // If triggered by bookmark but message is empty (shouldn't happen with the fix, but defensive)
      if (textToSubmit !== null && !messageToSend) {
        console.error("Bookmark submission attempted with empty content.");
      }
      return;
    }

    // Stop listening if active when submitting
    const wasListening = listening;
    if (wasListening) {
      //console.log("handleSubmit stopping listening...");
      await SpeechRecognition.stopListening();
      //console.log("handleSubmit stopped listening.");
    }

    // Clear input and reset transcript ONLY if it was a manual submission (e not null, textToSubmit is null)
    if (e && textToSubmit === null) {
      setInput('');
      setShouldRefocusInput(true); // Refocus only on manual submit
      resetTranscript(); // Reset transcript only on manual submit
    } else if (textToSubmit !== null) {
      // If submitted programmatically (like bookmark), optionally clear the input *after* use
      // setInput(''); // Uncomment this if you WANT the input cleared after bookmark submit
      // Or keep the input populated:
      // No action needed to keep input populated
    }


    // Add user message optimistically
    // Use messageToSend consistently
    // Check if it's the special "Generating..." message (which might be passed via textToSubmit now)
    if (messageToSend === 'Generating podcast script...') {
      setMessages(prev => {
        const generatingExists = prev.some(msg => msg.type === 'user' && msg.content === 'Generating podcast script...');
        return generatingExists ? prev : [...prev, { type: 'user', contentType: 'text', content: messageToSend }];
      });
    } else {
      // Add regular user messages
      setMessages(prev => [...prev, { type: 'user', contentType: 'text', content: messageToSend }]);
    }


    setLoading(true);
    setIsRecreatingContext(false); // Reset recreation flag

    // Reset isGeneratingScript state if it was set by the button click
    // Check messageToSend for the task prompt
    const isPodcastPromptSubmission = messageToSend.includes("TASK: Generate a podcast script");
    if (!isPodcastPromptSubmission && !messageToSend.includes("Generating podcast script...")) {
      setIsGeneratingScript(false); // Reset if it's a normal message
    }

    try {
      // Construct the payload conditionally using messageToSend
      const queryPayload = {
        contextId,
        query: messageToSend, // Use the determined message content
      };
      // Determine if it's a PathCast request based on messageToSend
      if (messageToSend.includes("TASK: Generate a podcast script")) {
        queryPayload.isPathCast = true; // Use the correct field name expected by the backend
      }


      // Make the API call with the payload
      const result = await queryContextCache(queryPayload);

      const rawResponse = result.data.response;
      let assistantMessage = null;

      // --- Start: Podcast/Text Response Handling ---
      const isPotentialPodcast = typeof rawResponse === 'object' && rawResponse !== null && rawResponse.podcast;

      if (isPotentialPodcast && queryPayload.isPathCast) {
        //console.log("PathCast JSON response detected.");
        assistantMessage = {
          type: 'assistant',
          contentType: 'podcast',
          content: rawResponse // Assume backend returns parsed object
        };
        //      console.log("PathCast script successfully received.");

        // Update the "Generating..." message
        setMessages(prev => prev.map(msg =>
          (msg.type === 'user' && msg.content === 'Generating podcast script...')
            ? assistantMessage
            : msg
        ).filter(msg => msg !== null));

      } else if (!queryPayload.isPathCast) {
        //        console.log("Handling as text response.");
        assistantMessage = {
          type: 'assistant',
          contentType: 'text',
          content: typeof rawResponse === 'string' ? rawResponse : JSON.stringify(rawResponse) // Ensure text content
        };
        setMessages(prev => [...prev, assistantMessage]);

      } else if (queryPayload.isPathCast && !isPotentialPodcast) {
        console.error("PathCast requested, but received non-JSON or incorrect format:", rawResponse);
        const errorContent = 'Error: Failed to generate PathCast in the expected format.';
        const errorMessage = { type: 'assistant', contentType: 'text', content: errorContent };
        // Update the "Generating..." message
        setMessages(prev => prev.map(msg =>
          (msg.type === 'user' && msg.content === 'Generating podcast script...')
            ? errorMessage
            : msg
        ).filter(msg => msg !== null));
      }
      // --- End: Podcast/Text Response Handling ---


      // Play sound only for non-podcast text messages or successful podcast generation
      if (assistantMessage && (assistantMessage.contentType === 'text' || assistantMessage.contentType === 'podcast')) {
        playSound();
      }

      setLoading(false);

      // NEW: save audio URL for playback UI
      if (result?.data?.audioUrl) {
        setPathCastAudioUrl(result.data.audioUrl);
      }

      if (result?.data?.loaderStatusId) {
        setLoaderStatusId(result.data.loaderStatusId);
      }

    } catch (error) {
      console.error('Error querying context:', error.code, error.message, error.details);

      // --- Start: Cache Not Found / Expired Handling ---
      if (error.code === 'functions/not-found' && error.message.includes('expired or could not be found')) {
        console.warn("Context cache expired or not found. Attempting recreation...");
        setMessages(prev => [...prev, { type: 'system', contentType: 'text', content: 'Session expired, attempting to refresh context...' }]);
        setIsRecreatingContext(true);

        try {
          if (!intelligenceFoundation) {
            throw new Error("Intelligence Foundation data is missing, cannot recreate context.");
          }
          const creationResult = await createContextCache({ intelligenceFoundation });
          const newContextId = creationResult.data.contextId;
          setContextId(newContextId);
          //console.log("New context created:", newContextId);

          setMessages(prev => prev.filter(msg => !(msg.type === 'system' && msg.content.includes('refresh context'))));

          //console.log("Retrying original query:", messageToSend); // Use messageToSend
          const retryPayload = {
            contextId: newContextId,
            query: messageToSend, // Use messageToSend
          };
          // Check messageToSend for the task
          if (messageToSend.includes("TASK: Generate a podcast script")) {
            retryPayload.isPathCast = true;
          }

          const retryResult = await queryContextCache(retryPayload);
          const retryRawResponse = retryResult.data.response;
          let retryAssistantMessage = null;
          const retryIsPotentialPodcast = typeof retryRawResponse === 'object' && retryRawResponse !== null && retryRawResponse.podcast;

          if (retryIsPotentialPodcast && retryPayload.isPathCast) {
            retryAssistantMessage = { type: 'assistant', contentType: 'podcast', content: retryRawResponse };
            setMessages(prev => prev.map(msg =>
              (msg.type === 'user' && msg.content === 'Generating podcast script...')
                ? retryAssistantMessage
                : msg
            ).filter(msg => msg !== null));
          } else if (!retryPayload.isPathCast) {
            retryAssistantMessage = { type: 'assistant', contentType: 'text', content: typeof retryRawResponse === 'string' ? retryRawResponse : JSON.stringify(retryRawResponse) };
            setMessages(prev => [...prev, retryAssistantMessage]);
          } else if (retryPayload.isPathCast && !retryIsPotentialPodcast) {
            //console.error("PathCast retry failed to return expected format:", retryRawResponse);
            const retryErrorContent = 'Error: Failed to generate PathCast after context refresh.';
            const retryErrorMessage = { type: 'assistant', contentType: 'text', content: retryErrorContent };
            setMessages(prev => prev.map(msg =>
              (msg.type === 'user' && msg.content === 'Generating podcast script...')
                ? retryErrorMessage
                : msg
            ).filter(msg => msg !== null));
          }

          if (retryAssistantMessage && (retryAssistantMessage.contentType === 'text' || retryAssistantMessage.contentType === 'podcast')) {
            playSound();
          }

        } catch (recreationError) {
          //console.error("Failed to recreate context or retry query:", recreationError);
          const errorMsgContent = `Failed to refresh the session: ${recreationError.message || 'Please try starting a new chat.'}`;
          const finalErrorMsg = { type: 'assistant', contentType: 'text', content: errorMsgContent };
          setMessages(prev => [...prev.filter(msg => !(msg.type === 'system' && msg.content.includes('refresh context'))), finalErrorMsg]); // Add error after removing system msg
          // Use messageToSend to check if it was a podcast attempt during failure
          const wasPodcastAttempt = messageToSend.includes("TASK: Generate a podcast script") || messageToSend === 'Generating podcast script...';
          setMessages(prev => prev.map(msg =>
            (msg.type === 'user' && msg.content === 'Generating podcast script...') // Still check the displayed message
              ? finalErrorMsg // Replace generating message with final error
              : msg
          ).filter(msg => msg !== null));
        } finally {
          setIsRecreatingContext(false);
        }
        // --- End: Cache Not Found / Expired Handling ---

      } else {
        // Handle other types of errors
        const displayError = error.message || 'Unknown error occurred';
        // Use messageToSend to check context for error message
        const wasPodcastAttempt = messageToSend.includes("TASK: Generate a podcast script") || messageToSend === 'Generating podcast script...';
        const errorContent = `Error generating podcast script: ${displayError}`;
        const generalErrorContent = `Sorry, I encountered an error processing your request: ${displayError}`;
        const finalErrorMsg = { type: 'assistant', contentType: 'text', content: wasPodcastAttempt ? errorContent : generalErrorContent };

        if (wasPodcastAttempt) {
          //console.error("Error occurred during queryContextCache call for podcast generation:", error);
          setMessages(prev => prev.map(msg =>
            (msg.type === 'user' && msg.content === 'Generating podcast script...') // Still check the displayed message
              ? finalErrorMsg
              : msg
          ).filter(msg => msg !== null));
        } else {
          setMessages(prev => [...prev, finalErrorMsg]);
        }
      }
      setLoading(false); // Ensure loading is always false outside the recreation block's finally
    } finally {
      // These flags are reset here *unless* recreation is happening
      if (!isRecreatingContext) {
        setIsGeneratingScript(false);
      }
      // setLoading should be managed within the try/catch/recreation blocks
    }
  }, [
    // Removed input from dependencies, added messageToSend logic inside
    contextId,
    listening,
    resetTranscript,
    setMessages,
    setLoading,
    setInput, // Keep setInput if needed for clearing or other purposes
    queryContextCache,
    playSound,
    intelligenceFoundation,
    createContextCache,
    setContextId,
    isGeneratingScript, // Keep this? Yes, needed inside.
    setIsGeneratingScript,
    textareaRef, // Keep textareaRef if focus logic remains relevant elsewhere
    setShouldRefocusInput, // Keep for manual submits
    setPathCastAudioUrl
  ]);

  // Define handleGeneratePodcastScript SECOND
  const handleGeneratePodcastScript = useCallback(async () => {
    if (!contextData || isGeneratingScript || loading) return;

    setIsGeneratingScript(true);

    const podcastPrompt = `
CONTEXT: You have deep knowledge about ${contextData.promptCompanyName}'s product ${contextData.promptProductName} and its relevance to ${contextData.promptCustomerName} (${contextData.promptCustomerRole}) facing the challenge "${contextData.promptCustomerProblem}".

TASK: Generate a podcast script based *only* on the context you possess. The script should follow the provided outline precisely.

PERSONAS & TONE: The podcast is hosted by two experienced GTM & Sales Gurus. They are educating a younger sales professional. The tone should be engaging, fast-paced, insightful, and focused on helping the salesperson connect the solution meaningfully to the customer's situation using the key points from the context.

OUTLINE:
1. Introduction
    a. Overview - the topics that will be discussed
2. Opportunity
    a. Why now, why change and why us (using context insights)
3. Segment Name* - matter of fact description of what will be covered
    a. Big Takeaway - max 3 sentences summarizing the key insight from context
    b. The Why - dive into key concepts/data from context supporting the takeaway
4. Wrap Up
    a. Recap - recap the big takeaways

* Repeat Segment Name section 3 times, using different relevant insights from the context for each.

OUTPUT FORMAT: Respond ONLY with a single, valid JSON object string. Do NOT include any other text, explanation, or markdown formatting outside the JSON. The JSON object should contain a single key, "script", whose value is a string formatted exactly like this example, using the generated content:

Example Format for the 'script' string value:
"# SEGMENT 1: INTRODUCTION\\n\\nHost1: Welcome back to Sales Velocity!\\nHost2: Today, we're diving deep into how folks like ${contextData.promptCustomerName} can tackle ${contextData.promptCustomerProblem} head-on.\\n\\n# SEGMENT 2: THE OPPORTUNITY - WHY ACT NOW?\\n\\nHost1: So, the big question is, why now? What's the urgency for ${contextData.promptCustomerName}?\\nHost2: Based on [mention specific trend/data from context], the cost of inaction is significant...\\n\\n# SEGMENT 3: UNDERSTANDING [Relevant Context Topic 1]\\n\\nHost1: Let's break down [Topic 1]. Big takeaway?\\nHost2: Simply put, [Summarize takeaway using context].\\nHost1: And the 'why' behind that?\\nHost2: It links directly to [Explain using context data/analysis]...\\n\\n# SEGMENT 4: LEVERAGING [Relevant Context Topic 2]\\n\\nHost1: Moving to [Topic 2]. What's the essence?\\nHost2: The key is [Summarize takeaway using context]...\\nHost1: Drill down into that...\\nHost2: When you look at [Explain using context data/analysis]...\\n\\n# SEGMENT 5: CONNECTING WITH [Relevant Context Topic 3]\\n\\nHost1: And finally, [Topic 3]?\\nHost2: This boils down to [Summarize takeaway using context]...\\nHost1: Explain that connection.\\nHost2: The data shows [Explain using context data/analysis]...\\n\\n# SEGMENT 6: WRAP UP\\n\\nHost1: Alright, quick recap...\\nHost2: Key takeaways are [Recap points]...\\nHost1: Thanks for tuning in!"

Generate the podcast script content based on the detailed business context you have for the ${contextData.promptCustomerName} scenario and format it strictly as the requested JSON object string.
    `.trim();

    setMessages(prev => [...prev, {
      type: 'user',
      contentType: 'text',
      content: 'Generating podcast script...'
    }]);

    // Call handleSubmit (which is now defined above)
    await handleSubmit(null, podcastPrompt);

    // Note: setIsGeneratingScript is reset inside handleSubmit's finally block

  }, [contextData, isGeneratingScript, loading, setMessages, setIsGeneratingScript]);


  useEffect(() => {
    if (prevListeningRef.current && !listening && finalTranscript.trim()) {
      //console.log("Auto-submitting final transcript:", finalTranscript);
      handleSubmit(null, finalTranscript);
    }

    prevListeningRef.current = listening;
  }, [listening, finalTranscript, handleSubmit]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handlePathLibraryOpen = () => {
    setIsPathLibraryOpen(true);
  };

  const handlePathLibraryClose = () => {
    setIsPathLibraryOpen(false);
  };

  const handleSelectPreset = (question) => {
    setInput(question);
    // Focus the textarea after selecting a preset
    setTimeout(() => {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        textarea.focus();
      }
    }, 100);
  };

  const handleCopyMessage = (content, contentType = 'text') => {
    try {

      logCustomEvent('chat_message_copied', {});

      if (contentType === 'text') {
        // Existing markdown-to-HTML copy logic
        const markdownToHtml = (md) => {
          return md
            .replace(/^### (.*$)/gim, '<h3>$1</h3>')
            .replace(/^## (.*$)/gim, '<h2>$1</h2>')
            .replace(/^# (.*$)/gim, '<h1>$1</h1>')
            .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/gim, '<em>$1</em>')
            .replace(/^\s*\* (.*$)/gim, '<ul><li>$1</li></ul>')
            .replace(/^\s*\d+\. (.*$)/gim, '<ol><li>$1</li></ol>')
            .replace(/\n/gim, '<br>')
            .replace(/<\/ul><ul>/gim, '')
            .replace(/<\/ol><ol>/gim, '');
        };
        const htmlContent = markdownToHtml(content);
        if (navigator.clipboard && window.ClipboardItem) {
          const blobHtml = new Blob([htmlContent], { type: 'text/html' });
          const blobText = new Blob([content], { type: 'text/plain' });
          const clipboardItem = new ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobText });
          navigator.clipboard.write([clipboardItem]).then(() => { /* copied feedback */ });
        } else {
          navigator.clipboard.writeText(content);
        }
      } else if (contentType === 'podcast') {
        // For podcasts, just copy the raw script text
        navigator.clipboard.writeText(content).then(() => { /* copied feedback */ });
      }

      // Visual feedback (optional enhancement)
      const copyBtn = document.activeElement;
      if (copyBtn && copyBtn.tagName === 'BUTTON') {
        const originalContent = copyBtn.innerHTML;
        copyBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check"><path d="M20 6 9 17l-5-5"/></svg>
        `; // Use a checkmark icon
        setTimeout(() => {
          copyBtn.innerHTML = originalContent;
        }, 1500);
      }

    } catch (err) {
      //console.error('Failed to copy message:', err);
      navigator.clipboard.writeText(content); // Fallback
    }
  };

  // Replace handleBookmark with this updated version
  const handleBookmark = (content) => {
    //logCustomEvent('chat_bookmark_clicked', {});
    setBookmarkContent(content);
    setIsBookmarkModalOpen(true);
  };

  // Add new handler for saving bookmark from modal
  const handleSaveBookmark = async (bookmarkData) => {
    logCustomEvent('chat_bookmark_saved', {});
    try {
      if (!user) {
        //console.error('No user is signed in');
        return;
      }

      // Add the 'order' field when creating a new bookmark
      // Give it a high number initially, or place it at the end based on current count
      const newOrder = allBookmarks.length; // Simple strategy: place at the end

      // Prepare the document data, excluding undefined fields
      const documentData = {
        userId: user.uid,
        userName: user.displayName || 'Anonymous User',
        content: bookmarkData.content,
        name: bookmarkData.name,
        timestamp: serverTimestamp(),
        contextId: contextId, // Make sure contextId is available and relevant here
        availability: bookmarkData.availability,
        icon: bookmarkData.icon,
        order: newOrder // Set initial order
      };

      // Only add favorite field if it's explicitly defined
      if (bookmarkData.favorite !== undefined) {
        documentData.favorite = bookmarkData.favorite;
      }

      await addDoc(collection(db, "apps"), documentData);

      // No need to manually fetch here, onSnapshot will update `allBookmarks`

      setIsBookmarkModalOpen(false);
    } catch (err) {
      console.error('Failed to save bookmark:', err);
      // TODO: Show error feedback
    }
  };

  // Wrap handleListen in useCallback
  const handleListen = useCallback(async () => {
    setMicError(null);
    if (!isMicrophoneAvailable) {
      console.error("Attempted to listen, but microphone is not available.");
      setMicError("Microphone not available. Check browser permissions.");
      return;
    }

    if (listening) {
      //console.log("Stopping listening...");
      try {
        await SpeechRecognition.stopListening();
        //console.log("Stopped listening.");
      } catch (error) {
        console.error("Error stopping listening:", error);
      }
    } else {
      //console.log("Starting listening...");
      resetTranscript();
      try {
        // Changed continuous to false as per previous logic, ensures it stops after pause
        await SpeechRecognition.startListening({ continuous: false });
        //console.log("Started listening.");
      } catch (error) {
        console.error("Error starting listening:", error);
        setMicError("Could not start listening. Please try again.");
      }
    }
  }, [isMicrophoneAvailable, listening, resetTranscript, setMicError]);

  // Add useEffect for keyboard shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        // Prevent default browser behavior (e.g., search)
        event.preventDefault();
        // Ensure the textarea doesn't have focus
        if (document.activeElement !== textareaRef.current) {
          //console.log("Keyboard shortcut triggered handleListen");
          handleListen();
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);

    // Cleanup function
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [handleListen]);

  // Check for browser support (only run on client-side)
  useEffect(() => {
    if (typeof window !== 'undefined' && !browserSupportsSpeechRecognition) {
      //console.warn("Browser doesn't support speech recognition.");
      setMicError("Speech recognition not supported by this browser.");
    }
  }, [browserSupportsSpeechRecognition]);

  // --- Fetch ALL Bookmarks (New) ---
  useEffect(() => {
    if (!user) {
      setAllBookmarks([]); // Clear bookmarks if user logs out
      return;
    }

    setIsBookmarksLoading(true);
    const q = query(
      collection(db, "apps"), // Assuming 'apps' is your bookmarks collection
      where("userId", "==", user.uid),
      orderBy("order", "asc"), // IMPORTANT: Order by the 'order' field for drag-and-drop
      orderBy("timestamp", "desc") // Secondary sort for items without an order yet
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const bookmarksData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllBookmarks(bookmarksData);
      setIsBookmarksLoading(false);
    }, (error) => {
      console.error("Error fetching all bookmarks: ", error);
      setIsBookmarksLoading(false);
      // Optional: Display error to user
    });

    // Cleanup listener on component unmount or user change
    return () => unsubscribe();
  }, [user]); // Re-run when user changes

  // Refresh favorites when all bookmarks change (covers additions/removals/updates)
  useEffect(() => {
    fetchFavoriteBookmarks();
  }, [allBookmarks, fetchFavoriteBookmarks]);

  // --- Bookmark Action Handlers (New/Updated for Manager) ---

  const handleToggleFavoriteBookmark = useCallback(async (id, isFavorite) => {
    if (!user) return;
    const bookmarkRef = doc(db, "apps", id);
    try {
      await updateDoc(bookmarkRef, { favorite: isFavorite });
      //console.log(`Bookmark ${id} favorite status updated to ${isFavorite}`);
      // No need to call fetchFavoriteBookmarks here, the main listener will update allBookmarks
      // and the useEffect above will trigger the favorite refresh.
    } catch (error) {
      //console.error("Error updating bookmark favorite status: ", error);
      // TODO: Show error feedback
    }
  }, [user]);

  const handleEditBookmark = useCallback(async (id, updatedData) => {
    if (!user) return;
    const bookmarkRef = doc(db, "apps", id);
    try {
      // Ensure timestamp isn't overwritten if it exists, or add if needed
      const dataToUpdate = { ...updatedData };
      // delete dataToUpdate.id; // Don't save the id within the document data itself
      // delete dataToUpdate.timestamp; // Prevent overwriting server timestamp if editing

      await updateDoc(bookmarkRef, dataToUpdate);
      //console.log(`Bookmark ${id} updated successfully.`);
    } catch (error) {
      //console.error("Error updating bookmark: ", error);
      // TODO: Show error feedback
      throw error; // Re-throw error so the manager modal knows it failed
    }
  }, [user]);

  const handleDeleteBookmark = useCallback(async (id) => {
    if (!user) return;
    const bookmarkRef = doc(db, "apps", id);
    try {
      await deleteDoc(bookmarkRef);
      //console.log(`Bookmark ${id} deleted successfully.`);
    } catch (error) {
      //console.error("Error deleting bookmark: ", error);
      // TODO: Show error feedback
    }
  }, [user]);

  const handleReorderBookmarks = useCallback(async (newOrderIds) => {
    if (!user) return;
    //console.log("Attempting to reorder bookmarks in Firestore:", newOrderIds);
    const batch = writeBatch(db);
    newOrderIds.forEach((id, index) => {
      const bookmarkRef = doc(db, "apps", id);
      // Update the 'order' field based on the new array index
      batch.update(bookmarkRef, { order: index });
    });

    try {
      await batch.commit();
      //console.log("Bookmark order updated successfully in Firestore.");
      // Note: The onSnapshot listener should automatically update the local state 'allBookmarks',
      // but the BookmarkManager also optimistically updates its internal order.
    } catch (error) {
      //console.error("Error updating bookmark order: ", error);
      // TODO: Show error feedback (potentially revert optimistic update in manager?)
    }
  }, [user]);

  // --- Define findStrongText Helper ---
  const findStrongText = useCallback((nodes) => {
    if (!nodes) return null;
    const nodesArray = React.Children.toArray(nodes);

    for (const node of nodesArray) {
      if (React.isValidElement(node)) {
        // Check the direct node type
        if (node.type === 'strong') {
          const textContent = React.Children.toArray(node.props.children)
            .map(child => typeof child === 'string' ? child : findStrongText([child]) || '') // Recursively get text from potential nested elements
            .join('');
          return textContent.trim();
        }
        // Check child nodes recursively only if the node itself is not strong
        // This prevents grabbing strong text from nested lists/tables incorrectly
        const foundInChildren = findStrongText(node.props.children);
        if (foundInChildren) {
          return foundInChildren;
        }
      } else if (typeof node === 'string') {
        // Continue searching sibling nodes
        continue;
      }
    }
    return null; // No strong tag found at this level or below
  }, []); // No dependencies needed for this helper

  // --- Define customMarkdownComponents INSIDE Chat component ---
  const customMarkdownComponents = {
    // Helper function to render the drill-down button
    DrillDownButton: () => {

      const handleClick = (selectedText) => {
        const prompt = `can you please drill down and explain the significance of the top 3 aspects of ` + selectedText;

        handleSubmit(null, prompt);
        // Add scroll to bottom logic here
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      };

      return (

        <button
          onClick={() => {
            const selectedText = window.getSelection()?.toString();
            if (selectedText) {
              navigator.clipboard.writeText(selectedText);
              // Add feedback if needed
              handleClick(selectedText);
            }
          }}
          className="hover:bg-white/5 text-base bg-black/10 p-3 rounded"
        >
          Deep Dive
        </button>
      );
    },

    ul: ({ node, ordered, ...props }) => {
      const items = React.Children.toArray(props.children).filter(
        (child) => typeof child !== 'string' || child.trim() !== ''
      );
      return (
        <div className="grid   gap-4 my-4">
          {items.map((item, index) => {
            const strongText = findStrongText(item.props.children);
            return (
              <div
              key={index}>
                {item}
                 </div>
            );
          })}
        </div>
      );
    },
    ol: ({ node, ordered, start = 1, ...props }) => {
      const items = React.Children.toArray(props.children).filter(
        (child) => typeof child !== 'string' || child.trim() !== ''
      );
      return (
        <div className="grid grid-cols-1 gap-4 my-4">
          {items.map((item, index) => {
            const strongText = findStrongText(item.props.children);
            return (
              <div
                key={index}
                
  
              >
                {item}
                 
              </div>
            );
          })}
        </div>
      );
    },
    // Override <li> to remove default list styling/markers.
    // We wrap in a Fragment to avoid adding extra DOM elements here.
    li: ({ node, ordered, ...props }) => {
      // Render children directly; the wrapper div is handled by ul/ol override
      return <>{props.children}</>;
    },
    // p: ({node, ...props}) => <p className="text-base" {...props} /> // Keep if needed

    // --- Table Overrides ---
    table: ({ node, ...props }) => (
      <div className="overfloxw-x-auto my-6">
        <table className="w-full min-w-full text-left    rounded-lg" {...props} />
      </div>
    ),
    thead: ({ node, ...props }) => (
      <thead className="bg-zinc-800/10" {...props} />
    ),
    tbody: ({ node, ...props }) => (
      <tbody className="divide-y divide-zinc-700/50" {...props} />
    ),
    tr: ({ node, isHeader, ...props }) => {
      // Find strong text within the row's direct children (td/th elements)
      const strongText = !isHeader ? findStrongText(props.children) : null;
      return (
        <tr className={`${isHeader ? '' : 'hover:bg-black/20'} relative group`} {...props}>
          {/* Render original cells */}
          {props.children}
          {/* Add an extra cell for the button if strong text found in data rows */}
          {!isHeader && (
            <td className="px-2 py-1 text-right align-top w-10">
              {/* Button appears on hover */}
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <customMarkdownComponents.DrillDownButton strongText={strongText} />
              </div>
            </td>
          )}
        </tr>
      );
    },
    th: ({ node, isHeader, ...props }) => (
      <th className="px-4 py-3 font-semibold text-sky-300 text-base" {...props} />
    ),
    td: ({ node, isHeader, ...props }) => (
      // Added padding-right to prevent text overlap with button cell
      <td className="px-4 py-3 text-base text-zinc-200 pr-12" {...props} />
    ),
    em: ({ node, ...props }) => (
      <span className="text-amber-400 italic contents" {...props} />
    ),
    // Add override for strong tags
    strong: ({ node, ...props }) => (
      <span className="text-sky-400 font-semibold contents" {...props} /> // Apply display: contents via Tailwind class
    )
  };

  // Effect to refocus the input after loading finishes
  useEffect(() => {
    // Only refocus if loading is finished AND the flag is set
    if (!loading && shouldRefocusInput) {
      textareaRef.current?.focus();
      setShouldRefocusInput(false); // Reset the flag immediately after focusing
    }
  }, [loading, shouldRefocusInput]); // Dependencies: run when loading or the flag changes

  //console.log('messages', messages);

  // Listener for loader status updates
  useEffect(() => {
    if (!loaderStatusId) return;

    const statusDocRef = doc(db, 'gen-loader-status', loaderStatusId);
    const unsub = onSnapshot(statusDocRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setLoaderStatusLabel(data.label);
        // Clear status and reset creating flag once complete or error
        if (data.status === 'complete' || data.status === 'error') {
          setIsCreatingPathCast(false);
          // Optionally delay removal of the banner/label
          setTimeout(() => setLoaderStatusLabel(null), 10000);
        }
      }
    });

    return () => unsub();
  }, [loaderStatusId]);

  // Handler for the new PathCast button
  const handleCreatePathCastClick = useCallback(async () => {
    if (!intelligenceFoundation) {
      console.error("handleCreatePathCastClick: intelligenceFoundation data is missing.");
      return;
    }
    if (isCreatingPathCast) return;

    console.log("handleCreatePathCastClick: Attempting to create PathCast...");
    setIsCreatingPathCast(true);
    logCustomEvent('create_pathcast_clicked', {});

    try {
      // Kick off the long-running Cloud Function. It should return quickly with a loaderStatusId
      const result = await createPathCastFirebase(
        { intelligenceFoundation },
        { timeout: 60000 } // keep the client call short – real work happens server-side
      );

      // Save audio URL or loaderStatusId, if returned immediately
      if (result?.data?.audioUrl) {
        setPathCastAudioUrl(result.data.audioUrl);
      }
      if (result?.data?.loaderStatusId) {
        setLoaderStatusId(result.data.loaderStatusId);
      }
    } catch (error) {
      // If we get a deadline-exceeded, we assume the backend job is still running – keep the loader active
      if (error.code === 'deadline-exceeded' || error.code === 'functions/deadline-exceeded') {
        console.warn('PathCast generation is taking longer – monitoring Firestore for updates…');
        try {
          // Attempt to find the most recent loader-status doc for this user (created within last 2 minutes)
          const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);
          const q = query(
            collection(db, 'gen-loader-status'),
            where('userId', '==', user.uid),
            where('createdAt', '>=', twoMinutesAgo),
            orderBy('createdAt', 'desc'),
            limit(1)
          );
          const snap = await getDocs(q);
          if (!snap.empty) {
            const docId = snap.docs[0].id;
            console.log(`Found recent loader-status doc ${docId}; starting listener.`);
            setLoaderStatusId(docId);
          } else {
            console.warn('No recent loader-status document found for user – UI will not auto-update.');
          }
        } catch (findErr) {
          console.error('Failed to locate recent loader-status document:', findErr);
        }
        // Do NOT reset isCreatingPathCast here; the loaderStatus listener will clear it when finished
      } else {
        console.error("Error calling createPathCast Firebase function:", error);
        setIsCreatingPathCast(false); // genuine failure – allow the user to retry
      }
    }
  }, [intelligenceFoundation, createPathCastFirebase, isCreatingPathCast, logCustomEvent, user]);

  return (
    <SkeletonTheme baseColor={baseColor} highlightColor={highlightColor}>

      <div className="flex h-full pb-0 rounded-t-lg overflow-hidden bg-zinc-900 text-zinc-100">
        {/* Left sidebar - question history */}
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{
            x: isSidebarOpen ? 0 : -280,
            opacity: isSidebarOpen ? 1 : 0,
            width: isSidebarOpen ? "18rem" : "0px"
          }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="bg-zinc-900/25 border-r border-zinc-800/25 p-4 hidden md:flex flex-col overflow-hidden"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-normal text-zinc-100 ml-2 mt-2 whitespace-normal min-w-[16rem]">Co-Creation</h2>
            <motion.button
              onClick={() => setIsSidebarOpen(false)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="p-1.5 rounded-full bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-400 -ml-8 hover:text-zinc-200 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </motion.button>
          </div>

          <div className="text-sm text-orange-300 -mt-2 mb-4 p-2 rounded-lg bg-oraxnge-900/10 whitespace-normal min-w-[16rem]">
            Design partner preview - please note that Coco is in technical preview and we look forward to your feedback!
          </div>

          {contextData && (
            <div className="mb-6 bg-blaxck/20 p-3 rounded-lg whitespace-normal min-w-[16rem]">
              <h1 className="text-lg font-normal mb-4 text-zinc-100 mt-2">Focus</h1>
              <h3 className="font-medium text-sky-300">{contextData.promptCustomerName}</h3>

              {/* <p className="text-sm text-zinc-200 mt-2">{contextData.promptCustomerName}</p> */}
              <p className="text-sm text-zinc-200 mt-4">{contextData.promptCustomerRole}</p>
              <p className="text-sm text-zinc-200 mt-1">{contextData.promptProductName}</p>
              <p className="text-sm text-zinc-200 mt-4">{contextData.promptCustomerProblem}</p>
            </div>
          )}

          <div className="space-y-4 overflow-y-auto">
            {messages
              .filter(msg => msg.type === 'user' && msg.content !== 'Generating podcast script...')
              .map((msg, index) => (
                <motion.div
                  key={`message-hist-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  className={`p-2 rounded-lg cursor-pointer p-3 text-sm ${selectedMessage === index
                    ? 'bg-sky-500/10 text-sky-300'
                    : 'bg-black/20 hover:bg-black/30 text-zinc-200'
                    }`}
                //onClick={() => setSelectedMessage(index)}
                >
                  {msg.content.length > 40
                    ? `${msg.content.substring(0, 40)}...`
                    : msg.content}
                </motion.div>
              ))}
          </div>
        </motion.div>

        {/* Toggle button for sidebar when closed */}
        {!isSidebarOpen && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            onClick={() => setIsSidebarOpen(true)}
            className="absolute left-0 top-[72px] z-10 p-2 rounded-r-lg bg-zinc-800/70 hover:bg-zinc-700/70 text-zinc-400 hover:text-zinc-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </motion.button>
        )}

        {/* Main chat area */}
        {/* Ensure this outer flex container manages height correctly */}
        <div className="flex-1 flex flex-col max-h-screen bg-zinc-900">
          {/* Chat messages container - Takes up remaining space and scrolls */}
          <div
            ref={chatContainerRef}
            // Removed relative and pb-40. Kept flex-1, overflow-y-auto. Added px-8 back here.
            className={`flex-1 overflow-y-auto px-8 pt-8 rounded-t-lg ${messages.some(msg => msg.type === 'system' && msg.contentType === 'loading')
              ? 'bg-gradient-to-t from-transparent to-sky-900/50 shadow-inner-lg xxshadow-sky-500/90'
              : ''
              } ${isRecreatingContext ? 'animate-pulse bg-yellow-900/20' : ''}`}
          >
            {/* Messages Wrapper */}
            <div className="space-y-8">
              <AnimatePresence>
                {messages.map((message, index) => (
                  <motion.div
                    key={`message-chat-${index}-${message.type}-${message.contentType}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    // Added some bottom margin to the last message to prevent touching the input box
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start items-start'} ${index === 0 && message.type === 'assistant' ? 'mt-8' : ''
                      } ${index === messages.length - 1 ? 'mb-4' : ''}`}
                  >
                    {message.type === 'system' ? (
                      <div
                        className="text-sm text-yellow-400 bg-yellow-900/30 px-3 py-1 rounded-full"
                      >
                        {message.content}
                      </div>
                    ) : (
                      <div className="flex items-start space-x-2 mr-12 w-full max-w-4xl">
                        <div
                          className={`flex-grow rounded-lg  py-0 ${message.contentType === 'podcast' ? 'bg-transparent'
                            : message.contentType === 'text' && message.content.startsWith('Error:') ? ' md:p-8 p-4  bg-red-900/30'
                              : ' md:p-8 p-4  bg-black/10'
                            }`}
                        >
                          <div className="prose prose-sm max-w-none text-lg prose-invert 
                                        prose-strong:!text-sky-400 
                                        prose-strong:!text-base
                                        prose-ol:!list-none 
                                        prose-li:!relative prose-li:!pl-2 
                                        
                                        before:!flex before:!items-center before:!justify-center 
                                        
                                        before:!bg-sky-500 before:!text-white before:!font-bold before:!text-sm">
                            {/* Apply custom components ONLY to text messages */}
                            {message.contentType === 'text' ? (
                              <div
                                className="
                                    prose prose-invert max-w-none text-lg
                                    prose-headings:text-sky-300
                                    prose-strong:text-sky-400
                                    prose-strong:!text-base
                                    prose-display:contents!important
                                    prose-em:contents!important prose-em:italic prose-em:text-amber-400 /* Ensure inline and amber color */
                                    prose-a:text-sky-400 hover:prose-a:text-sky-200
                                    prose-p:my-4 /* Remove vertical margins from paragraphs */
                            
                                    /* lists */
                                    prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-2
                                    prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-2
                                    prose-li:my-2
                            
                                    /* tables */
                                    prose-table:mt-6   
                                    prose-th:px-4 prose-th:py-2 prose-th:text-zinc-200 prose-th:font-normal  
                                       
                                    prose-td:px-4 prose-td:py-4  prose-td:border-zinc-600 prose-td:border-0.5

                                     selection:decoration-sky-500 selection:bg-blue-400/10 selection:text-sky-300 selection:font-bold selection:rounded-lg
                                  "
                              >
                                <Selection.Root>
                                  <Selection.Trigger asChild>
                                    <div>
                                      <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={customMarkdownComponents} >
                                        {message.content}
                                      </ReactMarkdown>
                                    </div>
                                  </Selection.Trigger>
                                  <Selection.Portal>
                                    <Selection.Content
                                      className="bg-zinc-800 text-white rounded-md p-2 shadow-lg text-sm shadow-black/50"
                                      // sideOffset={5}
                                      sideOffset={12}
                                    >
                                      <div className='flex gap-2 p-1'>
                                        <button
                                          onClick={() => {
                                            const selectedText = window.getSelection()?.toString();
                                            if (selectedText) {
                                              navigator.clipboard.writeText(selectedText);
                                              // Add feedback if needed
                                            }
                                          }}
                                          className="hover:bg-white/5  text-base bg-black/10 p-3 rounded"
                                        >
                                          Copy
                                        </button>
                                        <customMarkdownComponents.DrillDownButton />
                                      </div>
                                      <Selection.Arrow className="fill-zinc-800 " width={24} height={12} />
                                    </Selection.Content>
                                  </Selection.Portal>
                                </Selection.Root>
                              </div>
                            ) : message.contentType === 'podcast' ? (
                              // Handle podcast rendering (if different structure needed)
                              <div className="prose prose-sm max-w-none text-lg prose-invert ">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {message.content /* Or format podcast content appropriately */}
                                </ReactMarkdown>
                              </div>
                            ) : null /* Handle other content types if necessary */}
                          </div>

                        </div>
                        {index !== 0 && message.contentType !== 'podcast' && (
                          <motion.button
                            onClick={message.type === 'assistant'
                              ? () => handleCopyMessage(message.content, message.contentType)
                              : () => handleBookmark(message.content)}
                            whileHover={{ scale: 1.25 }}
                            className="group mt-2 rounded-full bg-black/20 hover:bg-white/5 h-10 w-10 min-w-[40px] min-h-[40px] transition-colors z-10 flex items-center justify-center shrink-0"
                            title={message.type === 'assistant' ? "Copy to clipboard" : "Save as App"}
                          >
                            {message.type === 'assistant' ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50 group-hover:opacity-100 transition-opacity">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50 group-hover:opacity-100 transition-opacity">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                              </svg>
                            )}
                          </motion.button>
                        )}
                      </div>
                    )}
                  </motion.div>
                ))}

                {loading && !isRecreatingContext && !messages.some(m => m.contentType === 'podcast') && !isGeneratingScript && (
                  <motion.div
                    key="loading-indicator"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start w-full"
                  >


                    <div className="bg-gradient-to-r from-[#121214] via-[#161618] to-[#121214] bg-[length:200%_100%] animate-shimmer rounded-lg md:p-8 p-2 flex items-center w-full max-w-4xl mr-12">
                      <div className="ml-4">
                        <div className="flex items-center">
                          <ThreeDotLoader />
                          {/* <span className="ml-4 text-lg">Absorbing your paths... ready in a moment</span> */}
                        </div>
                      </div>
                    </div>

                  </motion.div>
                )}

                {/* Loader status banner */}
                {loaderStatusLabel && (
                  <motion.div
                    key="loader-status"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start w-full"
                  >
                    <div className="bg-zinc-700/60 rounded-lg p-4 md:p-6 animate-pulse text-sky-200 w-full max-w-4xl mr-12">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-base md:text-lg">{loaderStatusLabel}</span>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Keep this inside the scrollable area but maybe add margin */}
                <div key="messages-end" ref={messagesEndRef} style={{ height: '1px' }} />
              </AnimatePresence>
            </div> {/* End Messages Wrapper */}
          </div> {/* End Scrollable Container */}

          {/* Input area - Sibling of the scrollable container, sits at the bottom */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            // Removed absolute positioning. Added padding px-8 py-4. Added relative for error positioning.
            className="px-8 pb-4 bg-zinc-900 relative"
          >
            {/* PathCast player UI */}
            {pathCastAudioUrl && <PathCastPlayer audioUrl={pathCastAudioUrl} />}

            {micError && (
              // Adjusted error positioning relative to the input wrapper
              <div className="absolute bottom-full left-8 right-8 mb-1 text-red-400 text-xs bg-red-900/50 p-2 rounded z-10">
                {micError}
              </div>
            )}

            <form onSubmit={(e) => handleSubmit(e)} className="relative flex flex-col ">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={listening ? "Listening..." : isRecreatingContext ? "Refreshing session..." : "Message..."}
                // Kept padding adjustments for buttons
                className={`w-full ${listening ? 'bg-gradient-to-r from-[#121214] via-[#161618] to-[#121214] bg-[length:200%_100%] animate-shimmer' : 'bg-black/20'} border border-zinc-800/50 text-zinc-200 rounded-xl p-6 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all duration-300 ease-in-out min-h-[96px] resize-none text-lg placeholder-zinc-500 pb-12  `}
                rows={4}
                disabled={loading || isGeneratingScript || isRecreatingContext}
              />

              {/* Buttons Container - Positioning remains the same relative to the form */}
              <div className="absolute bottom-4 left-4 flex items-center space-x-2 hidden sm:flex">
                {/* Path Library Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={handlePathLibraryOpen}
                  className="bg-zinc-400/5 hover:bg-sky-500/80 text-white p-3 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed
                  bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 bg-[length:200%_100%] animate-shimmer"
                  title="Path Library"
                  disabled={loading || isGeneratingScript || isRecreatingContext}
                >
                  <img src="/util/app/b3-admin-paths.svg" className="w-9 h-9 mr-2" />
                  <span className="hidden sm:inline">Compass</span>
                </motion.button>

                <div className="flex items-center space-x-2">
                  <BookmarkQuickLinks
                    bookmarks={favoriteBookmarks}
                    onSelectBookmark={(bookmark) => handleSelectBookmark(bookmark, true)}
                  //submitForm={true}
                  />
                </div>

                {/* Podcast Script Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={handleGeneratePodcastScript}
                  disabled={!contextData || loading || isGeneratingScript || isRecreatingContext}
                  className="p-2 bg-zinc-400/5 hover:bg-purple-500/80 text-white h-[50px] hidden  rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-zinc-400/5"
                  title={isGeneratingScript ? "Generating PathCast..." : "Generate PathCast"}
                >
                  {isGeneratingScript ? (
                    // Optional: Show a spinner or different icon during generation
                    <div className="flex items-center space-x-2 ">
                      <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="hidden sm:inline">Generating PathCast</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <img src="/util/app/b3-admin-podcast-a.svg" className="w-6 h-6" />
                      <span className="hidden sm:inline">Generate PathCast</span>
                    </div>
                  )}
                </motion.button>

                {/* New PathCast Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={handleCreatePathCastClick}
                  disabled={!intelligenceFoundation || loading || isGeneratingScript || isRecreatingContext || isCreatingPathCast}
                  className="p-2 bg-zinc-400/5 hover:bg-purple-500/80 text-white h-[50px] rounded-lg hidden items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-zinc-400/5"
                  title={isCreatingPathCast ? (loaderStatusLabel || "Creating PathCast...") : "Create PathCast"}
                >
                  {isCreatingPathCast ? (
                    <div className="flex items-center space-x-2 px-2">
                      <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="hidden sm:inline">{loaderStatusLabel || 'Creating...'}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 px-2">
                      <img src="/util/app/b3-admin-podcast-a.svg" className="w-6 h-6" />
                      <span className="hidden sm:inline">PathCast</span>
                    </div>
                  )}
                </motion.button>

              </div>


              {/* Buttons Container - Positioning remains the same relative to the form */}
              <div className="absolute bottom-4 right-4 flex items-center space-x-2">





                {/* Bookmarks Menu Button */}
                <div className="relative">
                  <BookmarkFavorites
                    isOpen={isBookmarksOpen}
                    bookmarks={favoriteBookmarks}
                    onSelectBookmark={handleSelectBookmark}
                    onOpenManager={() => setIsBookmarkManagerOpen(true)}
                  />

                  <HamburgerButton
                    isOpen={isBookmarksOpen}
                    onClick={toggleBookmarksMenu}
                  />
                </div>





                {/* Speech Recognition Button */}
                {browserSupportsSpeechRecognition && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={handleListen}
                    disabled={!isMicrophoneAvailable || loading || isGeneratingScript || isRecreatingContext}
                    className={`p-2 ${listening
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                      : 'bg-zinc-400/5 hover:bg-sky-500'
                      } text-white h-[50px] w-[50px] rounded-lg flex items-center justify-center transition-colors ${!isMicrophoneAvailable || loading || isGeneratingScript || isRecreatingContext ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={
                      !isMicrophoneAvailable
                        ? "Microphone not available"
                        : listening
                          ? "Stop Listening"
                          : "Start Listening (Cmd/Ctrl+K)"
                    }
                  >
                    <img src="/util/app/b3-admin-mic.svg" className="w-6 h-6" />
                  </motion.button>
                )}

                {/* Submit Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  disabled={!input.trim() || loading || isGeneratingScript || isRecreatingContext}
                  className="p-3 bg-zinc-400/5 hover:bg-sky-500 text-white h-[50px] w-[50px] rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-zinc-400/5"
                  title="Send Message (Enter)"
                >
                  <img src="/util/app/b3-admin-chat-submit-2.svg" className="w-7 h-7" />
                </motion.button>
              </div>
            </form>
          </motion.div> {/* End Input area */}
        </div> {/* End Main chat area */}

        {/* Path Library Modal */}
        <PathLibrary
          isOpen={isPathLibraryOpen}
          onClose={handlePathLibraryClose}
          onSelectPreset={handleSelectPreset}
        />

        {/* Add the Bookmark modal component before the end of the return */}
        <Bookmark
          isOpen={isBookmarkModalOpen}
          onClose={() => setIsBookmarkModalOpen(false)}
          onSave={handleSaveBookmark}
          //initialContent={bookmarkContent}
          initialPrompt={bookmarkContent}
        />

        {/* Add the BookmarkManager modal component */}
        <BookmarkManager
          isOpen={isBookmarkManagerOpen}
          onClose={() => setIsBookmarkManagerOpen(false)}
          bookmarks={allBookmarks}
          isLoading={isBookmarksLoading}
          onToggleFavorite={handleToggleFavoriteBookmark}
          onEdit={handleEditBookmark}
          onDelete={handleDeleteBookmark}
          onReorder={handleReorderBookmarks}
        />
      </div>
    </SkeletonTheme>
  );
};

export default Chat;